import { useState, useCallback } from 'react';
import { Dialog, Button, styled, TextField } from '@mui/material';
import { usePostComplaintMutation } from '../../queries';
import toast from 'react-hot-toast';
import IcRightArrow from '../../assets/image/ic_chevron_right.svg';
import IcX from '../../assets/image/ic_x.svg';
import useComplaintReasons from '../../hook/useComplaintReasons';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import FullHeightContainer from '../common/FullHeightContainer';
import OneBtnAlert from '../common/BottomOneBtnDialog';
import { TargetType } from '../../types';

const menuItemHeight = '48px';

const Accordion = styled(MuiAccordion)(() => ({
  padding: 0,
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#fff',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(() => ({
  height: menuItemHeight,
  display: 'block',
  width: '100%',
  padding: 0,
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  borderRadius: '8px',
  width: '100%',
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

interface ComplaintDialogProps {
  open: any;
  onClose: () => void;
  targetType: TargetType;
}

function ComplaintDialog({ open, onClose, targetType }: ComplaintDialogProps) {
  const complaintReasons = useComplaintReasons(open !== null);
  const [successAlert, setSuccessAlert] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [content, setContent] = useState('');

  const { mutate: postComplaint } = usePostComplaintMutation();

  const handleComplaintReport = useCallback(() => {
    const reasonCode = complaintReasons.data.find((reason) => reason.name === expanded)?.code;
    const apiParams = {
      targetType,
      targetId: open,
      reasonCode,
      details: content,
    };
    postComplaint(apiParams, {
      onSuccess: () => {
        setSuccessAlert(true);
      },
      onError: (error: any) => {
        console.error('Failed to submit complaint', error);
        toast.error(error?.response?.data?.message);
      },
    });
  }, [expanded, content, open, targetType, complaintReasons, postComplaint]);

  return (
    <Dialog open={open !== null} onClose={onClose} fullScreen>
      <FullHeightContainer>
        <div className="h-[48px] flex justify-center items-center w-full bg-white px-[20px] flex-grow-0 flex-shrink-0">
          <button type="button" className="w-[24px] h-[24px] cursor-pointer" onClick={onClose}>
            <img src={IcX} alt="back" />
          </button>
          <div className="text-[18px] leading-[19px] font-[700] text-[#2D2D2D] text-center flex-grow flex-shrink-1">
            신고하기
          </div>
          <div className="w-[24px] h-[24px]" style={{ visibility: 'hidden' }} />
        </div>
        <div className="pageInner">
          <div className="w-full font-[700] py-5 text-[16px]">신고하는 내용을 선택해주세요.</div>
          {complaintReasons?.data?.length >= 1 &&
            complaintReasons?.data?.map((reason) => {
              return (
                <Accordion
                  key={reason.code}
                  disableGutters
                  elevation={0}
                  square
                  expanded={expanded === reason.name}
                  onChange={(_event, newExpanded) => {
                    setExpanded(newExpanded ? reason.name : false);
                    setContent('');
                  }}
                >
                  <AccordionSummary aria-controls={`panel--content`} id={`panel`}>
                    <div className="w-full flex items-center justify-between">
                      <div className="text-[14px] font-[400] text-[#1A1A1A]">{reason?.name}</div>
                      <img
                        className="shrink-0 aspect-[0.96] w-[23px]"
                        loading="lazy"
                        src={IcRightArrow}
                        alt="icon"
                        style={{
                          transition: 'transform 0.2s ease',
                          transform: expanded === reason.name ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="w-full text-[#B3B3B3]">
                      <div className="w-full relative">
                        <TextField
                          placeholder="신고 내용을 입력해주세요. (최소 20자, 최대 300자)"
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={7}
                          value={content}
                          onChange={(e) => {
                            if (e.target.value.length < 300) {
                              setContent(e.target.value);
                            }
                          }}
                          sx={{ mb: 2 }}
                        />
                        <div className="absolute right-2 bottom-5 text-[12px] text-[#767676]">
                          {content.length}/300
                        </div>
                      </div>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={content.length < 20}
                        onClick={handleComplaintReport}
                      >
                        신고하기
                      </Button>
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </div>
        <OneBtnAlert
          open={successAlert}
          onClose={() => setSuccessAlert(false)}
          title="신고가 접수되었어요"
          description={`신고해 주셔서 감사합니다.\n신고된 내용은 검토하여 조치할 예정이에요.`}
          btnText="닫기"
          onClick={() => {
            onClose();
            setSuccessAlert(false);
          }}
        />
      </FullHeightContainer>
    </Dialog>
  );
}

export default ComplaintDialog;
